import { useEffect, type ComponentProps, type FunctionComponent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { trackPageLoad } from '@fff-web/fff-analytics';
import AccountDetailsPage from '../AccountDetails/AccountDetailsPage';
import InvoiceHistory from '../InvoiceHistory/InvoiceHistory';
import { SubscriptionStatus } from '../MyAccountPage';
import Orders from '../Orders';
import PersonalSurvey from '../PersonalSurvey/PersonalSurvey';
import RateAndReview from '../RateAndReview/RateAndReview';

type AccountDetailsPageProps = ComponentProps<typeof AccountDetailsPage> & {
  isDesktop: boolean;
  clubMemberSubscriptionId?: string | null;
  subscriptionState: SubscriptionStatus;
  refetchClubMemberSubscription: () => void;
};

const MyAccountRoutes: FunctionComponent<AccountDetailsPageProps> = ({
  firstName,
  lastName,
  shippingAddress,
  phone,
  email,
  credits,
  billingInfo,
  setBillingInfo,
  isDesktop,
  isClubMember,
  clubMemberSubscriptionId,
  subscriptionState,
  refetchClubMemberSubscription,
}) => {
  const { pathname } = useLocation();
  const { path } = useRouteMatch();
  const { myAccountOrderHistory: myAccountOrderHistoryFF, rateAndReview: rateAndReviewFF } =
    useFlags();
  const history = useHistory();

  // redirect user to /my-account/invoice-history if myAccountOrderHistory FF is OFF and access /my-account/orders
  useEffect(() => {
    const isOrdersPath = pathname.includes('orders');
    if (isOrdersPath && !myAccountOrderHistoryFF) {
      history.push('/my-account/invoice-history');
    }
  }, [myAccountOrderHistoryFF]);

  useEffect(() => {
    trackPageLoad({ pathName: pathname });
  }, [pathname]);

  return (
    <Switch>
      <Route exact path={path}>
        <AccountDetailsPage
          firstName={firstName}
          lastName={lastName}
          phone={phone}
          email={email}
          shippingAddress={shippingAddress}
          credits={credits}
          billingInfo={billingInfo}
          setBillingInfo={setBillingInfo}
          isClubMember={isClubMember}
          clubMemberSubscriptionId={clubMemberSubscriptionId}
          subscriptionState={subscriptionState}
          refetchClubMemberSubscription={refetchClubMemberSubscription}
        />
      </Route>
      <Route exact path={`${path}/orders`}>
        <Orders isDesktop={isDesktop} />
      </Route>
      {rateAndReviewFF && (
        <Route exact path={`${path}/rate-and-review`}>
          <RateAndReview />
        </Route>
      )}
      <Route exact path={`${path}/invoice-history`}>
        <InvoiceHistory />
      </Route>
      <Route exact path={`${path}/personal-survey`}>
        <PersonalSurvey />
      </Route>
    </Switch>
  );
};

export default MyAccountRoutes;
